import React, { useState, useEffect, useRef } from "react";
import {
  signIn,
  signUp,
  confirmSignUp,
  resendSignUpCode,
} from "aws-amplify/auth";
import { TermsModal, PrivacyPolicyModal, AppFooter } from "./LegalModals";

/* ═══════════════════════════════════════════════════════════════════
   SCENE DATA
═══════════════════════════════════════════════════════════════════ */

// English academic textbook text for the PDF panel (left side)
const PDF_LINES = [
  "Chapter 2 — Financial Markets & Instruments",
  "",
  "2.1 The Role of Financial Markets",
  "",
  "Financial markets facilitate the transfer of",
  "funds from surplus units (savers) to deficit",
  "units (borrowers). They provide mechanisms",
  "for price discovery and risk allocation.",
  "",
  "2.2 Classification of Assets",
  "",
  "Financial assets include securities, currency",
  "contracts, and derivative instruments. Real",
  "assets encompass tangible property such as",
  "land, buildings, and machinery. Securities",
  "are divided into debt and equity instruments.",
];

const EXPLAIN_SECTIONS = [
  {
    icon: "💡",
    title: "概念：现货市场",
    color: "#f59e0b",
    text: "现货市场是指买卖双方在交易中即时交割和支付的市场，通常涉及商品与金融资产的交易，交易以当前市场价格进行。",
  },
  {
    icon: "✅",
    title: "优点",
    color: "#10b981",
    text: "①即时性：交易完成后买家可立即获得资产。②透明性：价格公开，便于参与者决策。③高流动性：可快速买入或卖出资产。",
  },
  {
    icon: "⚠️",
    title: "局限性",
    color: "#ef4444",
    text: "①价格波动：受供需变化影响可能出现剧烈波动，增加交易风险。②信息不对称：部分参与者可能掌握更多市场信息。",
  },
];

const TRANS_LINES = [
  { en: "Today we'll discuss the efficient market hypothesis.", zh: "今天我们来讨论有效市场假说。" },
  { en: "Can anyone tell me what arbitrage means?", zh: "有人能解释一下套利的含义吗？" },
  { en: "Excellent. Prices reflect all available information.", zh: "很好。价格会反映所有可获得的信息。" },
];

/* ── Scene 0: PDF + AI Explain ──────────────────────────────────── */
function ScenePdfAI({ visible }) {
  const [sectionIdx, setSectionIdx] = useState(0);
  const [typed, setTyped] = useState("");
  const TABS = ["💡 解释", "📝 总结", "🌐 翻译", "💬 对话", "📋 笔记(8)"];

  useEffect(() => { if (!visible) return; setSectionIdx(0); setTyped(""); }, [visible]);

  useEffect(() => {
    if (!visible) return;
    const t = setInterval(() => setSectionIdx((i) => (i + 1) % EXPLAIN_SECTIONS.length), 2400);
    return () => clearInterval(t);
  }, [visible]);

  useEffect(() => {
    if (!visible) return;
    setTyped("");
    let i = 0;
    const full = EXPLAIN_SECTIONS[sectionIdx].text;
    const t = setInterval(() => { i++; setTyped(full.slice(0, i)); if (i >= full.length) clearInterval(t); }, 18);
    return () => clearInterval(t);
  }, [sectionIdx, visible]); // eslint-disable-line

  return (
    <div className="auth-scene-wrap" style={d.sceneWrap}>
      <div style={{ ...d.mockHeader, padding: "16px 12px" }}>
        <div style={d.mockHeaderLeft}>
        </div>
        <div style={d.mockHeaderRight}>
        </div>
      </div>

      <div className="auth-mock-body" style={d.mockBody}>
        {/* PDF side */}
        <div className="auth-pdf-side" style={d.pdfSide}>
          <div className="auth-pdf-toolbar" style={d.pdfToolbar}>
            <span style={d.pdfTool}>☰</span>
            <span style={d.pdfPageInfo}>257 / 632</span>
            <span style={d.pdfTool}>−</span>
            <span style={d.pdfZoom}>96%</span>
            <span style={d.pdfTool}>+</span>
            <span style={{ flex: 1 }} />
            <span style={d.pdfOpenBtn}>↗</span>
          </div>
          <div className="auth-pdf-content" style={d.pdfContent}>
            {PDF_LINES.map((line, i) => (
              <div key={i} style={{
                ...d.pdfLine,
                fontWeight: line.startsWith("Chapter") || line.match(/^\d+\.\d+ /) ? 700 : 400,
                fontSize: line.startsWith("Chapter") ? 11 : (line.match(/^\d+\.\d+ /) ? 10.5 : 10),
                marginBottom: line === "" ? 6 : 2,
                color: line.startsWith("Chapter") ? "#1e293b" : (line.match(/^\d+\.\d+ /) ? "#1d4ed8" : "#4b5563"),
              }}>
                {line}
              </div>
            ))}
          </div>
        </div>

        {/* AI panel side */}
        <div className="auth-ai-side" style={d.aiSide}>
          <div className="auth-ai-tabs" style={d.aiTabs}>
            {TABS.map((t, i) => (
              <div key={t} style={{ ...d.aiTab, ...(i === 0 ? d.aiTabActive : {}) }}>{t}</div>
            ))}
          </div>
          <div style={d.aiContent}>
            <p style={d.aiPromptLabel}>想解释哪个概念？</p>
            <div style={d.aiInputRow}>
              <span style={d.aiInputMock}>现货市场</span>
            </div>
            {EXPLAIN_SECTIONS.map((s, i) => (
              <div key={s.title} style={{ ...d.explainSection, opacity: i <= sectionIdx ? 1 : 0.15, transition: "opacity 0.4s" }}>
                <div style={{ ...d.explainHeading, color: s.color }}>{s.icon} {s.title}</div>
                <p style={d.explainText}>
                  {i === sectionIdx ? typed : s.text}
                  {i === sectionIdx && <span style={{ display: "inline-block", width: 1.5, height: "0.85em", background: s.color, verticalAlign: "middle", animation: "auth-blink 1s step-start infinite", marginLeft: 2 }} />}
                </p>
              </div>
            ))}
          </div>
          <div style={d.aiFooter}>
            <span style={d.addNoteBtn}>📌 一键加入笔记</span>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── Scene 1: Live Translation ───────────────────────────────────── */
function SceneLiveTranslation({ visible }) {
  const [visibleLines, setVisibleLines] = useState(0);
  const [typedZh, setTypedZh] = useState("");

  useEffect(() => {
    if (!visible) { setVisibleLines(0); setTypedZh(""); return; }
    let line = 0;
    const showNext = () => {
      if (line >= TRANS_LINES.length) { line = 0; setVisibleLines(0); setTypedZh(""); }
      line++;
      setVisibleLines(line);
      let i = 0;
      const full = TRANS_LINES[line - 1].zh;
      setTypedZh("");
      const tw = setInterval(() => {
        i++;
        setTypedZh(full.slice(0, i));
        if (i >= full.length) { clearInterval(tw); setTimeout(showNext, 1000); }
      }, 30);
    };
    const start = setTimeout(showNext, 300);
    return () => clearTimeout(start);
  }, [visible]); // eslint-disable-line

  return (
    <div className="auth-scene-wrap" style={d.sceneWrap}>
      <div style={d.transHeader}>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span style={{ color: "#fff", fontWeight: 700, fontSize: 12 }}>实时课堂翻译</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
          <span style={d.transDot} />
          <span style={{ color: "#fbbf24", fontSize: 10 }}>正在识别…</span>
        </div>
        <span style={{ color: "#64748b", fontSize: 10 }}>英语 → 中文</span>
      </div>

      <div className="auth-trans-body" style={d.transBody}>
        {visibleLines === 0 && (
          <div style={{ color: "#4b5563", fontSize: 11, textAlign: "center", marginTop: 36 }}>等待老师开始讲课…</div>
        )}
        {TRANS_LINES.slice(0, visibleLines).map((pair, i) => (
          <div key={i} style={{ ...d.transPair, animation: "auth-fadeUp 0.35s ease" }}>
            <div style={d.transRow}>
              <span style={d.langBadge}>EN</span>
              <span style={d.transEnText}>{pair.en}</span>
            </div>
            <div style={d.transRow}>
              <span style={{ ...d.langBadge, background: "#1d4ed8" }}>中</span>
              <span style={d.transZhText}>{i === visibleLines - 1 ? typedZh : pair.zh}</span>
            </div>
          </div>
        ))}
      </div>

      <div style={d.transControls}>
        <div style={{ display: "flex", justifyContent: "center", gap: 8 }}>
          <button style={d.stopBtn}>■ 停止</button>
          <button style={d.clearBtn}>清除</button>
        </div>
      </div>
    </div>
  );
}

/* ── Scene 2: Notes ──────────────────────────────────────────────── */
function SceneNotes({ visible }) {
  const [pulse, setPulse] = useState(false);
  const [justAdded, setJustAdded] = useState(false);
  const [highlighted, setHighlighted] = useState(false);

  useEffect(() => {
    if (!visible) { setPulse(false); setJustAdded(false); setHighlighted(false); return; }
    const t1 = setTimeout(() => setPulse(true), 600);
    const t2 = setTimeout(() => { setPulse(false); setJustAdded(true); }, 2000);
    const t3 = setTimeout(() => { setJustAdded(false); setHighlighted(true); }, 2400);
    const t4 = setTimeout(() => setHighlighted(false), 5000);
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); clearTimeout(t4); };
  }, [visible]);

  const folders = [
    {
      name: "General", count: 5, color: "#f59e0b",
      notes: [
        { title: "现货市场 vs 期货市场的区别", isNew: highlighted },
        { title: "有效市场假说三种形式", isNew: false },
      ],
    },
    {
      name: "Module 1", count: 3, color: "#6366f1",
      notes: [
        { title: "金融资产的分类与特征", isNew: false },
      ],
    },
  ];

  return (
    <div className="auth-scene-wrap" style={d.sceneWrap}>
      <div style={d.mockHeader}>
        <div style={d.mockHeaderLeft}>
        </div>
        <div style={d.mockHeaderRight}>
          <span style={{ color: "#93c5fd", fontSize: 9.5, fontWeight: 600 }}>📋 全部笔记</span>
        </div>
      </div>

      <div className="auth-notes-inner" style={{ background: "#f8fafc", flex: 1, overflowY: "auto", padding: "11px 12px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
          <span style={{ fontSize: 11, color: "#64748b", fontWeight: 600 }}>8 条笔记</span>
          <div style={{ display: "flex", gap: 6 }}>
            <span style={{ background: "#2563eb", color: "#fff", borderRadius: 6, padding: "3px 10px", fontSize: 10, fontWeight: 700 }}>+ 笔记</span>
            <span style={{ background: "#f59e0b", color: "#fff", borderRadius: 6, padding: "3px 10px", fontSize: 10, fontWeight: 700 }}>📁 文件夹</span>
          </div>
        </div>

        {folders.map((folder) => (
          <div key={folder.name} style={{ marginBottom: 8 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 7, padding: "6px 10px", background: "#fff", borderRadius: 8, border: "1px solid #e2e8f0", marginBottom: 3 }}>
              <span style={{ color: "#94a3b8", fontSize: 8 }}>▶</span>
              <span style={{ fontSize: 13 }}>📁</span>
              <span style={{ fontSize: 11, fontWeight: 600, color: "#374151", flex: 1 }}>{folder.name}</span>
              <span style={{ fontSize: 10, color: "#94a3b8", background: "#f1f5f9", borderRadius: 10, padding: "1px 7px" }}>{folder.count}</span>
            </div>
            {folder.notes.map((note, ni) => (
              <div key={ni} style={{
                marginLeft: 14, padding: "7px 10px",
                background: note.isNew ? "#eff6ff" : "#fff",
                border: `1px solid ${note.isNew ? "#93c5fd" : "#e2e8f0"}`,
                borderRadius: 7, marginBottom: 3,
                transition: "all 0.4s",
                boxShadow: note.isNew ? "0 0 0 2px rgba(37,99,235,0.35)" : "none",
              }}>
                <span style={{ fontSize: 10, fontWeight: 600, color: note.isNew ? "#1d4ed8" : "#374151" }}>
                  {note.isNew && "✨ "}{note.title}
                </span>
              </div>
            ))}
          </div>
        ))}

        {justAdded && (
          <div style={{ animation: "auth-fadeUp 0.3s ease", background: "#d1fae5", border: "1px solid #6ee7b7", borderRadius: 8, padding: "8px 12px", fontSize: 10, color: "#065f46", textAlign: "center", fontWeight: 600 }}>
            ✅ 已成功加入笔记！
          </div>
        )}

        <div style={{
          marginTop: 10, padding: "7px 12px",
          background: pulse ? "linear-gradient(135deg,#2563eb,#1d4ed8)" : "#f1f5f9",
          borderRadius: 8, textAlign: "center",
          transition: "all 0.4s",
          border: `1px solid ${pulse ? "transparent" : "#e2e8f0"}`,
          cursor: "pointer",
        }}>
          <span style={{ fontSize: 10, fontWeight: 700, color: pulse ? "#fff" : "#64748b" }}>
            {pulse ? "⭐ 正在加入笔记…" : "📌 点击一键加入笔记"}
          </span>
        </div>
      </div>
    </div>
  );
}
/* ── Demo Showcase wrapper ────────────────────────────────────────── */
const SCENE_LABELS = [
  { icon: "🎙️", label: "实时课堂翻译", desc: <>英文课也能听得懂，<span style={{ color: "#2563eb", fontWeight: 700 }}>网课也适用</span></> },
  { icon: "📚", label: "课本 AI 解释", desc: <>秒懂任意段落，<span style={{ color: "#2563eb", fontWeight: 700 }}>课本与AI并排呈现</span></> },
  { icon: "📌", label: "一键记笔记", desc: <>AI解释与总结，<span style={{ color: "#2563eb", fontWeight: 700 }}>一键加入笔记</span></> },
];

const SCENE_BENEFITS = [
  "英文授课实时翻译成中文，网课也适用",
  "AI 即时解释课本段落，课本与 AI 对话并排呈现",
  "从课本或翻译结果一键保存笔记 · 考前直接复习",
];

function DemoShowcase() {
  const [scene, setScene] = useState(0);
  const [fading, setFading] = useState(false);

  useEffect(() => {
    const t = setInterval(() => {
      setFading(true);
      setTimeout(() => { setScene((s) => (s + 1) % 3); setFading(false); }, 380);
    }, 7000);
    return () => clearInterval(t);
  }, []);

  const switchScene = (idx) => {
    if (idx === scene) return;
    setFading(true);
    setTimeout(() => { setScene(idx); setFading(false); }, 380);
  };

  return (
    <div style={{ width: "100%" }}>
      {/* Feature tabs */}
      <div className="auth-demo-tabs" style={{ display: "flex", gap: 10, marginBottom: 16 }}>
        {SCENE_LABELS.map(({ icon, label, desc }, i) => (
          <button
            key={label}
            onClick={() => switchScene(i)}
            style={{
              flex: 1, padding: "5px 6px",
              background: scene === i ? "#ffffff" : "#f8fafc",
              border: `2px solid ${scene === i ? "#2563eb" : "#e2e8f0"}`,
              borderRadius: 12, cursor: "pointer",
              color: scene === i ? "#1d4ed8" : "#64748b",
              transition: "all 0.2s", textAlign: "center",
              boxShadow: "none",
            }}
          >
            <div className="auth-demo-tab-icon" style={{ fontSize: 18, marginBottom: 2 }}>{icon}</div>
            <div className="auth-demo-tab-label" style={{ fontSize: 11.5, fontWeight: 700, marginBottom: 1 }}>{label}</div>
            <div className="auth-demo-tab-desc" style={{ fontSize: 10, color: scene === i ? "#3b82f6" : "#94a3b8", lineHeight: 1.3 }}>{desc}</div>
          </button>
        ))}
      </div>

      {/* Demo window */}
      <div style={{
        borderRadius: 14, overflow: "hidden",
        border: `2px solid ${"#dbeafe"}`,
        boxShadow: "none",
        opacity: fading ? 0 : 1,
        transform: fading ? "translateY(8px)" : "translateY(0)",
        transition: "opacity 0.35s, transform 0.35s",
      }}>
        {scene === 0 && <SceneLiveTranslation visible={!fading && scene === 0} />}
        {scene === 1 && <ScenePdfAI visible={!fading && scene === 1} />}
        {scene === 2 && <SceneNotes visible={!fading && scene === 2} />}
      </div>

      {/* Benefit caption */}
      <div style={{
        marginTop: 12, padding: "11px 16px",
        background: "linear-gradient(135deg, #eff6ff, #dbeafe)",
        border: "1px solid #bfdbfe", borderRadius: 10,
        textAlign: "center",
        animation: "auth-fadeUp 0.3s ease",
      }}>
        <span style={{ fontSize: 12.5, color: "#1d4ed8", fontWeight: 600 }}>✦ {SCENE_BENEFITS[scene]}</span>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════
   FAQ ITEM
═══════════════════════════════════════════════════════════════════ */
function FaqItem({ q, a }) {
  const [open, setOpen] = React.useState(false);
  return (
    <div style={{ borderBottom: "1px solid #f1f5f9", paddingBottom: 10, marginBottom: 10 }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{ width: "100%", textAlign: "left", background: "none", border: "none", cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center", padding: "2px 0", gap: 8 }}
      >
        <span style={{ fontSize: 13, fontWeight: 600, color: "#1e293b" }}>{q}</span>
        <span style={{ fontSize: 16, color: "#94a3b8", flexShrink: 0, transform: open ? "rotate(180deg)" : "none", transition: "transform 0.2s" }}>▾</span>
      </button>
      {open && (
        <p style={{ margin: "8px 0 0", fontSize: 12, color: "#64748b", lineHeight: 1.65 }}>{a}</p>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════
   MAIN AUTH PAGE
═══════════════════════════════════════════════════════════════════ */
export default function AuthPage({ onSignIn }) {
  const [mode, setMode] = useState("login");
  const [email, setEmail]       = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode]         = useState("");
  const [error, setError]       = useState("");
  const [loading, setLoading]   = useState(false);
  const [pendingEmail, setPendingEmailState] = useState(
    () => sessionStorage.getItem("notemila-pending-email") || ""
  );
  const [showTerms, setShowTerms] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);

  const setPendingEmail = (val) => {
    setPendingEmailState(val);
    if (val) sessionStorage.setItem("notemila-pending-email", val);
    else sessionStorage.removeItem("notemila-pending-email");
  };

  // If we have a pending email from a previous session, go straight to confirm
  useEffect(() => {
    const saved = sessionStorage.getItem("notemila-pending-email");
    if (saved) setMode("confirm");
  }, []); // eslint-disable-line

  const clearError = () => setError("");

  const handleLogin = async (e) => {
    e.preventDefault(); setError(""); setLoading(true);
    try {
      await signIn({ username: email, password });
      onSignIn();
    } catch (err) {
      if (err.name === "UserNotConfirmedException") { setPendingEmail(email); setMode("confirm"); }
      else setError(err.message || "登录失败，请检查邮箱和密码。");
    } finally { setLoading(false); }
  };

  const handleRegister = async (e) => {
    e.preventDefault(); setError("");
    if (password.length < 6) { setError("密码至少需要6位字符。"); return; }
    setLoading(true);
    try {
      await signUp({ username: email, password, options: { userAttributes: { email } } });
      setPendingEmail(email); setMode("confirm");
    } catch (err) {
      if (err.name === "UsernameExistsException") {
        // Account exists but likely unconfirmed — send them to confirm screen
        setPendingEmail(email); setMode("confirm");
        try { await resendSignUpCode({ username: email }); setError("验证码已重新发送到您的邮箱。"); }
        catch { setError("账户已存在，请前往验证邮箱，或尝试直接登录。"); }
      } else {
        setError(err.message || "注册失败，请稍后重试。");
      }
    } finally { setLoading(false); }
  };

  const handleConfirm = async (e) => {
    e.preventDefault(); setError(""); setLoading(true);
    try {
      await confirmSignUp({ username: pendingEmail, confirmationCode: code });
      await signIn({ username: pendingEmail, password });
      setPendingEmail("");
      onSignIn();
    } catch (err) { setError(err.message || "验证失败，请检查验证码。"); }
    finally { setLoading(false); }
  };

  const handleResend = async () => {
    setError("");
    try { await resendSignUpCode({ username: pendingEmail }); setError("新验证码已发送到您的邮箱。"); }
    catch (err) { setError(err.message || "发送失败，请稍后重试。"); }
  };

  const pageRef = useRef(null);
  useEffect(() => { if (pageRef.current) pageRef.current.scrollTop = 0; }, []);

  return (
    <>
      {showTerms && <TermsModal onClose={() => setShowTerms(false)} />}
      {showPrivacy && <PrivacyPolicyModal onClose={() => setShowPrivacy(false)} />}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Orbitron:wght@700&display=swap');
        @keyframes auth-blink { 0%,100%{opacity:1} 50%{opacity:0} }
        @keyframes auth-fadeUp { from{opacity:0;transform:translateY(12px)} to{opacity:1;transform:translateY(0)} }
        .auth-input:focus { border-color: #2563eb !important; box-shadow: 0 0 0 3px rgba(37,99,235,0.15) !important; outline: none; }
        .auth-input::placeholder { color: #c0cfe0; font-size: 12px; }
        .auth-btn-primary:hover:not(:disabled) { filter: brightness(1.07); transform: translateY(-1px); box-shadow: 0 8px 28px rgba(37,99,235,0.42) !important; }
        .auth-btn-primary:disabled { opacity: 0.65; cursor: not-allowed; }
        .auth-link-btn:hover { text-decoration: underline; }
        .auth-mobile-hero { display: none; }
        .auth-mobile-faq { display: none; }
        @media (max-width: 768px) {
          /* Vertical stack */
          .auth-body { flex-direction: column !important; overflow: visible !important; flex: none !important; min-height: unset !important; }
          /* Demo col: full width, no right border */
          .auth-demo-col {
            flex: none !important;
            overflow-y: visible !important;
            border-right: none !important;
            border-bottom: 1px solid #e2e8f0 !important;
            padding: 20px 16px 16px !important;
          }
          /* Compact scene tabs */
          .auth-demo-tabs > button { padding: 8px 5px !important; }
          .auth-demo-tab-icon { font-size: 20px !important; }
          .auth-demo-tab-label { font-size: 11px !important; }
          .auth-demo-tab-desc { display: none !important; }
          /* Scene wrapper fixed height on mobile */
          .auth-scene-wrap { height: 265px !important; }
          /* Side-by-side on mobile: hide toolbar noise, keep both columns */
          .auth-mock-header { display: none !important; }
          .auth-pdf-toolbar { display: none !important; }
          .auth-pdf-side { flex: 0 0 40% !important; border-right: 1px solid #e2e8f0 !important; }
          .auth-pdf-content { padding: 8px 8px !important; }
          .auth-pdf-content > div { font-size: 8.5px !important; line-height: 1.5 !important; margin-bottom: 1px !important; }
          /* AI side: hide all tabs except the active first one */
          .auth-ai-tabs { padding: 0 !important; min-height: unset !important; }
          .auth-ai-tabs > div:not(:first-child) { display: none !important; }
          .auth-ai-tabs > div:first-child { font-size: 10px !important; padding: 5px 8px !important; }
          .auth-ai-side { flex: 1 !important; }
          /* Form col: full width, natural flow */
          .auth-form-col {
            flex: none !important;
            overflow-y: visible !important;
            padding: 20px 16px 32px !important;
            align-items: stretch !important;
            justify-content: flex-start !important;
          }
          /* Flat card on mobile */
          .auth-form-card {
            box-shadow: none !important;
            border: 1px solid #e2e8f0 !important;
            padding: 24px 20px !important;
            border-radius: 16px !important;
          }
          /* Touch targets */
          .auth-input { min-height: 48px !important; font-size: 16px !important; }
          .auth-btn-primary { min-height: 50px !important; font-size: 16px !important; }
          /* Hide static mobile hero (now replaced by real demo) */
          .auth-mobile-hero { display: none !important; }
          .auth-mobile-faq { display: none !important; }
          .auth-header-pill { display: none !important; }
        }
      `}</style>

      <div ref={pageRef} style={f.page}>
        {/* Header */}
        <header style={f.header}>
          <div style={f.headerLogo}>
            <span style={f.headerLogoName}>
              <span style={{ color: "#1d4ed8" }}>NOTE</span>
              <span style={{ color: "#3b82f6" }}>MILA</span>
            </span>
          </div>
          <div className="auth-header-pill" style={f.trialPill}>🎉 7天免费体验 · 无需绑定信用卡</div>
        </header>

        <div className="auth-body" style={f.body}>
          {/* Demo section */}
          <div className="auth-demo-col" style={f.demoCol}>
            {/* Hero */}
            <div style={{ marginBottom: 8 }}>
              <div style={{ fontSize: 28, fontWeight: 800, color: "#0f172a", letterSpacing: "-0.02em", lineHeight: 1.2, marginBottom: 8 }}>
                AI 智能学习助手
              </div>
              <div style={{ fontSize: 13, color: "#64748b" }}>
                课本AI解释 &nbsp;·&nbsp; 老师课堂实时翻译 &nbsp;·&nbsp; 一键智能记笔记
              </div>
              <div style={{ marginTop: 10, fontSize: 12, color: "#6366f1", display: "flex", alignItems: "center", gap: 5, fontWeight: 500 }}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ flexShrink: 0 }}>
                  <circle cx="12" cy="12" r="10" stroke="#6366f1" strokeWidth="2"/>
                  <path d="M8 12l3 3 5-5" stroke="#6366f1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                Powered by OpenAI Language Model
              </div>
            </div>

            <DemoShowcase />

            {/* Pricing */}
            <div style={f.pricingStrip}>
              <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 14 }}>
                <div style={{ fontSize: 32, fontWeight: 800, color: "#0f172a" }}>$19.9</div>
                <div style={{ fontSize: 13, color: "#64748b", lineHeight: 1.4 }}>
                  <div>每月</div>
                  <div style={{ fontSize: 11, color: "#94a3b8" }}>7天免费，随时取消</div>
                </div>
              </div>
            </div>

            {/* FAQ */}
            <div style={{ marginTop: 24, borderTop: "1px solid #e2e8f0", paddingTop: 20 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: "#94a3b8", letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 12 }}>常见问题</div>
              {[
                { q: "所用的AI模型是什么？", a: "NoteMila 使用 OpenAI 模型，为您提供摘要、翻译、问答等所有 AI 功能。" },
                { q: "我的文件安全吗？", a: "所有文件加密存储于 AWS 澳洲数据中心，我们不会将您的数据用于训练或共享给第三方。" },
              ].map(({ q, a }) => (
                <FaqItem key={q} q={q} a={a} />
              ))}
            </div>
          </div>

          {/* Right: form */}
          <div className="auth-form-col" style={f.formCol}>

            {/* Mobile-only hero */}
            <div className="auth-mobile-hero" style={{ marginBottom: 20, paddingBottom: 20, borderBottom: "1px solid #e2e8f0" }}>
              <div style={{ fontSize: 26, fontWeight: 800, color: "#0f172a", lineHeight: 1.25, marginBottom: 10 }}>
                AI 智能学习助手 🎓
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 14 }}>
                {["📖 课本AI解释", "🎙️ 实时翻译", "📋 智能笔记"].map(tag => (
                  <span key={tag} style={{ background: "#eff6ff", color: "#1d4ed8", fontSize: 12, fontWeight: 600, padding: "5px 12px", borderRadius: 20, border: "1px solid #bfdbfe" }}>{tag}</span>
                ))}
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <span style={{ fontSize: 24, fontWeight: 800, color: "#0f172a" }}>$19.9</span>
                <span style={{ fontSize: 12, color: "#64748b", lineHeight: 1.5 }}>/月<br/><span style={{ color: "#10b981", fontWeight: 600 }}>🎉 7天免费，随时取消</span></span>
              </div>
            </div>

            <div className="auth-form-card" style={f.formCard}>

              {/* Mode tabs */}
              <div style={f.modeTabs}>
                {[["login", "登录"], ["register", "注册"]].map(([m, label]) => (
                  <button
                    key={m}
                    style={{ ...f.modeTab, ...(mode === m || (mode === "confirm" && m === "register") ? f.modeTabActive : {}) }}
                    onClick={() => { setMode(m); clearError(); }}
                  >
                    {label}
                  </button>
                ))}
              </div>

              {/* Login */}
              {mode === "login" && (
                <div key="login" style={{ animation: "auth-fadeUp 0.3s ease" }}>
                  <p style={f.subtitle}>欢迎回来，继续您的学习之旅 👋</p>
                  <form onSubmit={handleLogin} style={f.form}>
                    <label style={f.label}>邮箱</label>
                    <input className="auth-input" style={f.input} type="email" placeholder=""
                      value={email} onChange={(e) => { clearError(); setEmail(e.target.value); }} required autoFocus />
                    <label style={f.label}>密码</label>
                    <input className="auth-input" style={f.input} type="password" placeholder=""
                      value={password} onChange={(e) => { clearError(); setPassword(e.target.value); }} required />
                    {error && <div style={f.error}>{error}</div>}
                    <button className="auth-btn-primary" style={f.btnPrimary} type="submit" disabled={loading}>
                      {loading ? "登录中…" : "登录"}
                    </button>
                  </form>
                </div>
              )}

              {/* Register */}
              {mode === "register" && (
                <div key="register" style={{ animation: "auth-fadeUp 0.3s ease" }}>
                  <p style={f.subtitle}>创建账户，开启智能学习新体验 🚀</p>
                  <form onSubmit={handleRegister} style={f.form}>
                    <label style={f.label}>邮箱</label>
                    <input className="auth-input" style={f.input} type="email" placeholder=""
                      value={email} onChange={(e) => { clearError(); setEmail(e.target.value); }} required autoFocus />
                    <label style={f.label}>密码</label>
                    <input className="auth-input" style={f.input} type="password" placeholder="至少6位"
                      value={password} onChange={(e) => { clearError(); setPassword(e.target.value); }} required />
                    {error && <div style={f.error}>{error}</div>}
                    <button className="auth-btn-primary" style={f.btnPrimary} type="submit" disabled={loading}>
                      {loading ? "创建账户中…" : "免费开始体验 →"}
                    </button>
                  </form>
                  <p style={f.legal}>注册即表示您同意我们的{" "}<button className="auth-link-btn" style={{ ...f.linkBtn, fontSize: 11 }} onClick={() => setShowTerms(true)}>Terms &amp; Conditions</button>{" "}与{" "}<button className="auth-link-btn" style={{ ...f.linkBtn, fontSize: 11 }} onClick={() => setShowPrivacy(true)}>Privacy Policy</button>。</p>
                </div>
              )}

              {/* Confirm */}
              {mode === "confirm" && (
                <div key="confirm" style={{ animation: "auth-fadeUp 0.3s ease" }}>
                  <div style={{ fontSize: 40, textAlign: "center", marginBottom: 8 }}>📬</div>
                  <p style={f.subtitle}>
                    验证码已发送至 <strong style={{ color: "#2563eb" }}>{pendingEmail}</strong>，请查收邮件。
                  </p>
                  <form onSubmit={handleConfirm} style={f.form}>
                    <label style={f.label}>6位验证码</label>
                    <input
                      className="auth-input"
                      style={{ ...f.input, letterSpacing: "0.4em", textAlign: "center", fontSize: 22, fontWeight: 700 }}
                      type="text" placeholder="000000"
                      value={code}
                      onChange={(e) => { clearError(); setCode(e.target.value.replace(/\D/g, "").slice(0, 6)); }}
                      required autoFocus inputMode="numeric" maxLength={6}
                    />
                    {error && <div style={f.error}>{error}</div>}
                    <button className="auth-btn-primary" style={f.btnPrimary} type="submit" disabled={loading}>
                      {loading ? "验证中…" : "验证并登录"}
                    </button>
                  </form>
                  <p style={{ color: "#64748b", fontSize: 13, marginTop: 16, textAlign: "center" }}>
                    未收到验证码？{" "}
                    <button className="auth-link-btn" style={f.linkBtn} onClick={handleResend}>重新发送</button>
                  </p>
                </div>
              )}

              {/* Testimonial */}
              <div style={f.testimonial}>
                <div style={f.testimonialQuote}>
                  "NoteMila 让我在国外上课再也不迷茫，老师讲什么都能看懂，笔记整理也超方便！"
                </div>

              </div>
            </div>

            {/* Mobile-only FAQ */}
            <div className="auth-mobile-faq" style={{ marginTop: 24, paddingTop: 20, borderTop: "1px solid #e2e8f0" }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: "#94a3b8", letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 12 }}>常见问题</div>
              {[
                { q: "所用的AI模型是什么？", a: "NoteMila 使用 OpenAI 模型，为您提供摘要、翻译、问答等所有 AI 功能。" },
                { q: "我的文件安全吗？", a: "所有文件加密存储于 AWS 澳洲数据中心，我们不会将您的数据用于训练或共享给第三方。" },
              ].map(({ q, a }) => (
                <FaqItem key={q} q={q} a={a} />
              ))}
            </div>

          </div>
        </div>
        {/* Footer */}
        <AppFooter onShowTerms={() => setShowTerms(true)} onShowPrivacy={() => setShowPrivacy(true)} />
      </div>
    </>
  );
}

/* ═══════════════════════════════════════════════════════════════════
   STYLES
═══════════════════════════════════════════════════════════════════ */
const f = {
  page: {
    position: "fixed", inset: 0,
    display: "flex", flexDirection: "column",
    background: "#ffffff", zIndex: 1000,
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    overflowY: "auto",
  },
  header: {
    background: "#ffffff",
    padding: "12px 28px",
    display: "flex", alignItems: "center", justifyContent: "space-between",
    flexShrink: 0,
    borderBottom: "1px solid #e2e8f0",
    boxShadow: "none",
  },
  headerLogo: { display: "flex", alignItems: "center", gap: 12 },
  headerLogoImg: {
    height: 40, width: 40, borderRadius: "50%",
    filter: "drop-shadow(0 0 10px rgba(59,130,246,0.45))",
  },
  headerLogoName: {
    fontFamily: "'Orbitron', sans-serif",
    fontSize: "1.35rem", fontWeight: 700, letterSpacing: "0.12em",
  },
  trialPill: {
    background: "#eff6ff",
    border: "1px solid #bfdbfe",
    color: "#1d4ed8", fontSize: 12, fontWeight: 600,
    borderRadius: 20, padding: "5px 14px",
  },
  body: { flex: 1, display: "flex", minHeight: 0 },
  demoCol: {
    flex: "0 0 58%",
    padding: "30px 36px 30px 40px",
    display: "flex", flexDirection: "column", gap: 20,
    background: "#f8fafc",
    borderRight: "1px solid #e2e8f0",
    overflowY: "auto",
  },
  pricingStrip: {
    background: "#ffffff",
    border: "1px solid #e2e8f0",
    borderRadius: 14, padding: "16px 18px",
  },
  pricingTitle: {
    color: "#94a3b8", fontSize: 11, fontWeight: 600,
    textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 12,
  },
  formCol: {
    flex: "0 0 42%",
    display: "flex", flexDirection: "column",
    alignItems: "center", justifyContent: "center",
    padding: "32px 36px", overflowY: "auto",
    background: "#ffffff",
  },
  formCard: {
    width: "100%", maxWidth: 400,
    background: "#ffffff", borderRadius: 20,
    padding: "36px 32px",
    boxShadow: "none",
    border: "1px solid #dbeafe",
  },
  modeTabs: {
    display: "flex", borderBottom: "1px solid #e2e8f0",
    marginBottom: 20,
  },
  modeTab: {
    flex: 1, padding: "10px 0",
    background: "none", border: "none",
    color: "#94a3b8", fontSize: 15, fontWeight: 600, cursor: "pointer",
    borderBottom: "2px solid transparent",
    transition: "color 0.2s, border-color 0.2s",
  },
  modeTabActive: { color: "#2563eb", borderBottomColor: "#2563eb" },
  trialBanner: {
    background: "linear-gradient(135deg, #eff6ff, #dbeafe)",
    border: "1px solid #bfdbfe",
    borderRadius: 10, padding: "9px 14px",
    color: "#1e40af", fontSize: 13, marginBottom: 16, textAlign: "center",
  },
  subtitle: { color: "#64748b", fontSize: 14, margin: "0 0 18px", lineHeight: 1.5 },
  form: { display: "flex", flexDirection: "column", gap: 10 },
  label: { color: "#1e3a5f", fontSize: 13, fontWeight: 600, marginBottom: 2 },
  input: {
    padding: "11px 14px",
    background: "#f8fbff", border: "1.5px solid #dbeafe",
    borderRadius: 10, color: "#1e293b", fontSize: 15, outline: "none",
    width: "100%", boxSizing: "border-box",
    transition: "border-color 0.2s, box-shadow 0.2s",
  },
  error: {
    color: "#dc2626", fontSize: 13,
    background: "#fef2f2", border: "1px solid #fecaca",
    borderRadius: 8, padding: "8px 12px",
  },
  btnPrimary: {
    padding: "13px",
    background: "linear-gradient(135deg, #2563eb, #1d4ed8)",
    color: "#fff", border: "none", borderRadius: 10,
    fontSize: 15, fontWeight: 700, cursor: "pointer", marginTop: 6,
    boxShadow: "0 4px 16px rgba(37,99,235,0.32)",
    transition: "filter 0.2s, transform 0.15s, box-shadow 0.2s",
  },
  legal: { color: "#94a3b8", fontSize: 11, marginTop: 14, textAlign: "center", lineHeight: 1.6 },
  linkBtn: {
    background: "none", border: "none",
    color: "#2563eb", fontWeight: 600, cursor: "pointer", fontSize: 13, padding: 0,
  },
  testimonial: {
    marginTop: 22, padding: "14px 16px",
    background: "linear-gradient(135deg, #eff6ff, #dbeafe)",
    borderRadius: 12, borderLeft: "3px solid #2563eb",
  },
  testimonialQuote: {
    fontSize: 12, color: "#374151", lineHeight: 1.65,
    fontStyle: "italic", marginBottom: 6,
  },
  testimonialAuthor: { fontSize: 11, color: "#1d4ed8", fontWeight: 600 },
};

const d = {
  sceneWrap: { background: "#fff", display: "flex", flexDirection: "column", height: 380 },
  mockHeader: {
    background: "linear-gradient(135deg, #1d4ed8 0%, #2563eb 100%)",
    padding: "7px 12px", display: "flex", alignItems: "center", justifyContent: "space-between",
  },
  mockHeaderLeft: { display: "flex", alignItems: "center", gap: 7 },
  mockHeaderTitle: {
    fontFamily: "'Orbitron', sans-serif",
    fontSize: 10.5, fontWeight: 700, letterSpacing: "0.15em",
  },
  mockHeaderRight: { display: "flex", alignItems: "center", gap: 6 },
  mockChip: {
    background: "rgba(37,99,235,0.08)", border: "1px solid #dbeafe",
    borderRadius: 6, padding: "2px 8px", color: "#2563eb", fontSize: 9, fontWeight: 500,
  },
  mockBtn: {
    background: "rgba(37,99,235,0.08)", border: "1px solid #dbeafe",
    borderRadius: 6, padding: "2px 8px", color: "#2563eb", fontSize: 9, fontWeight: 500,
  },
  mockBody: { display: "flex", flex: 1 },
  pdfSide: {
    flex: "0 0 43%", borderRight: "1px solid #e2e8f0",
    display: "flex", flexDirection: "column", background: "#fafbfc",
  },
  pdfToolbar: {
    display: "flex", alignItems: "center", gap: 6,
    padding: "5px 10px", borderBottom: "1px solid #e2e8f0", background: "#f1f5f9",
  },
  pdfTool: { fontSize: 10, color: "#64748b", cursor: "pointer" },
  pdfPageInfo: { fontSize: 10, color: "#475569", fontWeight: 600 },
  pdfZoom: { fontSize: 10, color: "#475569", fontWeight: 600 },
  pdfOpenBtn: { fontSize: 9, background: "#2563eb", color: "#fff", borderRadius: 5, padding: "2px 7px", fontWeight: 600 },
  pdfContent: { flex: 1, padding: "12px 14px", overflowY: "hidden" },
  pdfLine: { color: "#374151", lineHeight: 1.65, fontSize: 10 },
  aiSide: { flex: 1, display: "flex", flexDirection: "column" },
  aiTabs: {
    display: "flex", borderBottom: "1px solid #e2e8f0",
    padding: "0 6px", background: "#fafbfc", overflowX: "auto",
  },
  aiTab: {
    padding: "7px 10px", background: "none", border: "none",
    borderBottom: "2px solid transparent",
    fontSize: 10.5, fontWeight: 600, color: "#94a3b8", cursor: "pointer", whiteSpace: "nowrap",
  },
  aiTabActive: { color: "#2563eb", borderBottomColor: "#2563eb" },
  aiContent: { flex: 1, padding: "9px 11px", overflowY: "auto" },
  aiPromptLabel: { color: "#64748b", fontSize: 11, fontWeight: 600, margin: "0 0 6px" },
  aiInputRow: { marginBottom: 10 },
  aiInputMock: {
    background: "#f1f5f9", border: "1px solid #e2e8f0",
    borderRadius: 5, padding: "4px 10px", fontSize: 11, color: "#475569", display: "inline-block",
  },
  explainSection: { marginBottom: 9 },
  explainHeading: { fontSize: 11, fontWeight: 700, marginBottom: 3 },
  explainText: { fontSize: 10.5, color: "#374151", lineHeight: 1.6, margin: 0 },
  aiFooter: {
    display: "flex", gap: 6, padding: "6px 10px",
    borderTop: "1px solid #e2e8f0", background: "#fafbfc",
  },
  readAloudBtn: {
    flex: 1, textAlign: "center", padding: "6px",
    background: "#f1f5f9", borderRadius: 6, fontSize: 10.5, color: "#64748b", cursor: "pointer",
  },
  addNoteBtn: {
    flex: 1, textAlign: "center", padding: "6px",
    background: "linear-gradient(135deg, #2563eb, #1d4ed8)",
    borderRadius: 6, fontSize: 10.5, color: "#fff", fontWeight: 700, cursor: "pointer",
  },
  transHeader: {
    background: "#0f172a", padding: "8px 14px",
    display: "flex", alignItems: "center", justifyContent: "space-between",
  },
  transDot: {
    width: 7, height: 7, borderRadius: "50%",
    background: "#fbbf24", display: "inline-block",
    animation: "auth-blink 1.5s step-start infinite",
  },
  transBody: {
    background: "#0f172a", padding: "12px 16px", flex: 1,
    display: "flex", flexDirection: "column", gap: 10, overflowY: "auto",
  },
  transPair: {
    background: "#1e293b", borderRadius: 10,
    padding: "10px 14px", display: "flex", flexDirection: "column", gap: 6,
    border: "1px solid rgba(255,255,255,0.08)",
  },
  transRow: { display: "flex", alignItems: "flex-start", gap: 8 },
  langBadge: {
    background: "#1d4ed8", color: "#bfdbfe",
    fontSize: 9.5, fontWeight: 700, borderRadius: 4,
    padding: "2px 6px", flexShrink: 0, marginTop: 1,
  },
  transEnText: { fontSize: 12, color: "#cbd5e1", lineHeight: 1.5 },
  transZhText: { fontSize: 12, color: "#93c5fd", lineHeight: 1.5 },
  transControls: {
    background: "#0f172a", padding: "10px 14px",
    borderTop: "1px solid rgba(255,255,255,0.06)",
  },
  stopBtn: {
    background: "#ef4444", color: "#fff", border: "none",
    borderRadius: 20, padding: "5px 16px", fontSize: 10, fontWeight: 700, cursor: "pointer",
  },
  clearBtn: {
    background: "#334155", color: "#94a3b8", border: "none",
    borderRadius: 20, padding: "5px 14px", fontSize: 10, fontWeight: 600, cursor: "pointer",
  },
  saveBtn: {
    background: "linear-gradient(135deg, #2563eb, #1d4ed8)", color: "#fff", border: "none",
    borderRadius: 20, padding: "5px 14px", fontSize: 10, fontWeight: 700, cursor: "pointer",
  },
};
